import { ZoomlogiNewColorLogo } from '@/assets/icons/icons';
import { WhiteLogo1 } from '@/assets/images';
import { CustomShipmentText } from '@/components';
import { Col, Flex, Row } from 'antd';
import React, { memo } from 'react';
import whiteLogo from '../../assets/images/cardinal-icons/Logo.svg';
import LoginFormImg from '../../assets/images/loginFormImg.png';
import themeStyle from '../../assets/theme';
import AuthHeader from '../../components/AuthHeader';

interface AuthPageProps {
  title?: string;
  description?: string;
  imageSrc?: string;
  imageOnLeft?: boolean;
  pageName: 'Signup' | 'Login';
  children?: React.ReactNode;
}

/**
 * Authentication page layout component
 *
 * Renders the authentication page with header, title, description, and optional image.
 * Supports both Signup and Login page variants.
 *
 * @param title - The title text to display
 * @param description - The description text to display
 * @param imageSrc - The source URL for the image
 * @param imageOnLeft - Whether the image should be on the left side (default: true)
 * @param pageName - The page type ('Signup' or 'Login')
 * @param children - Additional components or elements to render within the content section
 */
const AuthPage = memo(function AuthPage({
  title = '',
  description = '',
  imageSrc = null,
  imageOnLeft = true,
  pageName,
  children = null,
}: AuthPageProps) {
  const theme = themeStyle;
  const isZoomLogi = theme.themeType === 1;
  const whiteLogoSrc = WhiteLogo1;
  return (
    <div className='auth-container zl-auth-main'>
      {pageName === 'Signup' ? (
        <>
          <AuthHeader
            bgColor={theme.primary}
            color={theme.themeType === 1 ? theme.color : theme.text}
            logo={theme.themeType === 1 ? whiteLogoSrc : whiteLogo}
            isSharePage={false}
          />

          <div className='auth-middle-container'>
            <Flex
              className='signup-container'
              gap={82}
              align='center'
              justify='space-between'
            >
              {imageOnLeft && (
                <div className='image-container'>
                  <img src={imageSrc} alt='' />
                </div>
              )}

              <div className='auth-content'>
                <Flex
                  gap={24}
                  vertical={true}
                  className='auth-signup-title-group'
                >
                  <CustomShipmentText
                    text={title}
                    color='var(--text-primary)'
                    fontSize='40px'
                    fontWeight={900}
                    lineHeight='120%'
                    marginBottom={'0'}
                    className='auth-signup-title'
                  />

                  {description && (
                    <CustomShipmentText
                      fontSize='16px'
                      lineHeight='24px'
                      text={description}
                      marginBottom={'32px'}
                      className='auth-signup-sub-text'
                    />
                  )}
                </Flex>
                {children}
              </div>

              {!imageOnLeft && (
                <div className='image-container'>
                  <img src={imageSrc} alt='' />
                </div>
              )}
            </Flex>
          </div>
        </>
      ) : null}

      {pageName === 'Login' ? (
        <Flex align='center' className='zl-login-section w-full zl-login-outer'>
          <Flex
            className='zl-login-section w-full zl-login-inner'
            vertical={true}
          >
            <Row className='w-full zl-login-row' align='stretch'>
              <Col xs={0} md={12} lg={14} className='login-image-col'>
                <Flex className='zl-login-logo' gap={6} align='center'>
                  {theme.themeType === 1 ? (
                    <>
                      <img
                        src={whiteLogoSrc}
                        width={26}
                        className='login-header-logo'
                        alt=''
                      />
                      <CustomShipmentText
                        text={theme.title}
                        color={'#FAF9F6'}
                        fontSize='28px'
                        lineHeight='28px'
                        fontWeight='700'
                      />
                    </>
                  ) : (
                    <img
                      src={theme.headerLogo}
                      width={theme.loginLogoWidth}
                      className='header-logo'
                      alt=''
                    />
                  )}
                </Flex>
                <div
                  className={`login-image relative ${
                    isZoomLogi ? 'login-image--zoom' : 'login-image--tinted'
                  }`}
                  style={
                    !isZoomLogi
                      ? ({
                          '--login-image-bg': theme?.primary,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  {isZoomLogi && (
                    <img
                      src={LoginFormImg}
                      className='zl-login-bg-img'
                      alt='Login'
                    />
                  )}
                </div>
              </Col>

              <Col xs={24} md={12} lg={10} className='flex-center x y'>
                <div>
                  <Flex
                    gap={6}
                    align='center'
                    className='login-company-mobile-logo'
                  >
                    {theme.themeType === 1 ? (
                      <Flex gap={6} align='center'>
                        <ZoomlogiNewColorLogo />
                        <CustomShipmentText
                          text={theme.title}
                          color={theme.text}
                          fontSize='28px'
                          lineHeight='28px'
                          fontWeight='700'
                          marginBottom='0px'
                        />
                      </Flex>
                    ) : (
                      <img
                        src={theme.headerLogo}
                        width={theme.loginLogoWidth}
                        alt=''
                      />
                    )}{' '}
                  </Flex>
                  <main>{children}</main>
                </div>
              </Col>
            </Row>
          </Flex>
        </Flex>
      ) : null}
    </div>
  );
});

AuthPage.displayName = 'AuthPage';
export default AuthPage;
